/**
 * Member Operations Configuration
 *
 * This file maintains a list of member-related GraphQL operations that should trigger
 * token refresh checks before execution.
 *
 * When a member performs any of these operations, the system will check if the
 * access token will expire within 5 minutes, and if so, automatically refresh it.
 *
 * IMPORTANT: This list must be kept in sync with actual GraphQL operations.
 * If you add a new member-related query or mutation, you MUST add it to the
 * appropriate array below, otherwise token refresh will not work for that operation.
 *
 * How to add new member operations:
 * 1. Identify if it's a query (read operation) or mutation (write operation)
 * 2. Add the operation name to MEMBER_QUERIES or MEMBER_MUTATIONS array
 * 3. The operation name should match the GraphQL operation name exactly
 *    (e.g., "getCurrentMember", "createMemberOrder")
 * 4. The comparison is case-insensitive, so "GetCurrentMember" and "getCurrentMember" both work
 *
 * Example:
 * - Query: "getCurrentMember" -> add to MEMBER_QUERIES
 * - Mutation: "createMemberOrder" -> add to MEMBER_MUTATIONS
 */

/**
 * Member-related queries that require authentication
 * These are read operations that fetch member data
 */
export const MEMBER_QUERIES = [
    'getCurrentMember',
    'retrieveMemberOrderHistory',
    'retrieveMemberUpcomingOrders',
    'checkPendingRatings',
]

/**
 * Member-related mutations that require authentication
 * These are write operations that modify member data or create member resources
 */
export const MEMBER_MUTATIONS = ['createMemberOrder', 'submitDishRatings', 'cancelMemberOrder']

/**
 * Check if an operation is a member-related operation
 * @param {string} operationName - The GraphQL operation name
 * @returns {boolean} whether the operation is member-related
 */
export const isMemberOperation = operationName => {
    if (!operationName) {
        return false
    }

    // Normalize operation name to lowercase for comparison
    // GraphQL operation names can be in different cases (e.g., "CreateMemberOrder" vs "createMemberOrder")
    const normalizedName = operationName.toLowerCase()

    // Check against normalized member operations
    const normalizedQueries = MEMBER_QUERIES.map(q => q.toLowerCase())
    const normalizedMutations = MEMBER_MUTATIONS.map(m => m.toLowerCase())

    return normalizedQueries.includes(normalizedName) || normalizedMutations.includes(normalizedName)
}

/**
 * Get all member operations for reference
 * @returns {Object} object containing all member operations categorized by type
 */
export const getAllMemberOperations = () => {
    return {
        queries: [...MEMBER_QUERIES],
        mutations: [...MEMBER_MUTATIONS],
        all: [...MEMBER_QUERIES, ...MEMBER_MUTATIONS],
    }
}

const memberOperations = {
    MEMBER_QUERIES,
    MEMBER_MUTATIONS,
    isMemberOperation,
    getAllMemberOperations,
}

export default memberOperations
